import { DEFAULT_PAGE_SIZE } from "@/constants/config";
import { ORDER_STATUSES, ORDER_STATUS_LABELS, type OrderStatus } from "@/constants/orders";
import { workspaceOrderPath, workspaceSectionPath } from "@/constants/routes";
import { PageHeader } from "@/components/shared/page-header";
import { Pagination } from "@/components/shared/pagination";
import { EmptyState, ErrorState, LoadingState } from "@/components/shared/states";
import { OrderStatusBadge, SettlementBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/field";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs } from "@/components/ui/tabs";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useOrders } from "@/features/orders/api";
import { formatCurrency, formatDate } from "@/lib/format";
import { useWorkspace } from "@/providers/workspace-provider";
import { ShoppingCart } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

const STATUS_TABS = [
  { value: "all" as const, label: "All" },
  { value: ORDER_STATUSES.unpaid, label: ORDER_STATUS_LABELS.unpaid },
  { value: ORDER_STATUSES.partiallyPaid, label: ORDER_STATUS_LABELS.partially_paid },
  { value: ORDER_STATUSES.paid, label: ORDER_STATUS_LABELS.paid },
  { value: ORDER_STATUSES.cancelled, label: ORDER_STATUS_LABELS.cancelled },
];

/** `/w/:slug/orders` — order list with status filter and pagination. */
export const OrdersPage = () => {
  const { workspace } = useWorkspace();
  const [status, setStatus] = useState<OrderStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search);

  const ordersQuery = useOrders(workspace.id, {
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    status: status === "all" ? undefined : status,
    q: debouncedSearch.length > 0 ? debouncedSearch : undefined,
  });

  const items = ordersQuery.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Orders"
        description="Every order in this workspace, with its settlement state."
        actions={
          <Button asChild>
            <Link to={workspaceSectionPath(workspace.slug, "pos")}>
              <ShoppingCart className="size-4" aria-hidden />
              New order
            </Link>
          </Button>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs
          items={STATUS_TABS}
          value={status}
          onValueChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        />
        <Input
          placeholder="Search customer…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="sm:max-w-xs"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {ordersQuery.isLoading ? <LoadingState /> : null}
          {ordersQuery.isError ? (
            <ErrorState
              description={ordersQuery.error instanceof Error ? ordersQuery.error.message : undefined}
              onRetry={() => void ordersQuery.refetch()}
            />
          ) : null}
          {!ordersQuery.isLoading && items.length === 0 ? (
            <EmptyState
              title="No orders yet"
              description="Create the first order from the POS."
              action={
                <Button asChild>
                  <Link to={workspaceSectionPath(workspace.slug, "pos")}>Open POS</Link>
                </Button>
              }
            />
          ) : null}
          {items.length > 0 && ordersQuery.data ? (
            <div className="p-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Settlement</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <Link
                          to={workspaceOrderPath(workspace.slug, order.id)}
                          className="font-medium text-primary"
                        >
                          {order.code}
                        </Link>
                      </TableCell>
                      <TableCell>{order.customer_name}</TableCell>
                      <TableCell>
                        <SettlementBadge type={order.settlement_type} />
                      </TableCell>
                      <TableCell>
                        <OrderStatusBadge status={order.status} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(order.total_amount, order.currency)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(order.balance_amount, order.currency)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(order.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                page={ordersQuery.data.page}
                pageSize={ordersQuery.data.page_size}
                total={ordersQuery.data.total}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </>
  );
};
